import React, { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';
import { ReflexFeedbackRating } from 'shiftNotes/_types';

import * as Styled from 'shiftNotes/styles/NotesStack.style';

interface StepRatingControlsProps {
  rating: ReflexFeedbackRating | null;
  comment: string;
  onRatingChange: (rating: ReflexFeedbackRating) => void;
  onCommentChange: (comment: string) => void;
  disabled?: boolean;
}

export default function StepRatingControls({
  rating,
  comment,
  onRatingChange,
  onCommentChange,
  disabled = false,
}: StepRatingControlsProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');

  const handleRatingClick =
    (nextRating: ReflexFeedbackRating) => (event: MouseEvent) => {
      event.stopPropagation();
      onRatingChange(nextRating);
    };

  const handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onCommentChange(event.target.value);
  };

  return (
    <>
      {rating === 'negative' && (
        <Styled.TextArea
          value={comment}
          onChange={handleCommentChange}
          onClick={(event: MouseEvent) => event.stopPropagation()}
          onKeyDown={(event: KeyboardEvent) => event.stopPropagation()}
          placeholder={t('reflexFeedback.improvePlaceholder')}
          aria-label={t('reflexFeedback.improvePlaceholder')}
          disabled={disabled}
        />
      )}
      <Styled.FeedbackRow>
        <Styled.FeedbackThumbButton
          type="button"
          $selected={rating === 'negative'}
          onClick={handleRatingClick('negative')}
          aria-pressed={rating === 'negative'}
          aria-label={t('reflexFeedback.thumbsDown')}
          disabled={disabled}
        >
          <General.ThumbsDown />
        </Styled.FeedbackThumbButton>
        <Styled.FeedbackThumbButton
          type="button"
          $selected={rating === 'positive'}
          onClick={handleRatingClick('positive')}
          aria-pressed={rating === 'positive'}
          aria-label={t('reflexFeedback.thumbsUp')}
          disabled={disabled}
        >
          <General.ThumbsUp />
        </Styled.FeedbackThumbButton>
      </Styled.FeedbackRow>
    </>
  );
}
